// ============================================================================
// GOOGLE ANALYTICS 4 SERVICE - Track user engagement and game events
// ============================================================================
// Provides automatic tracking for page views, sessions, countries
// Plus custom events for game-specific metrics

// Declare gtag function type
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// GA4 Measurement ID (from Firebase config)
const GA_MEASUREMENT_ID = 'G-QNLH88NB94';

// Custom event names for the game
export enum GameAnalyticsEvent {
  // Session events
  GameStart = 'game_start',
  GameEnd = 'game_end',
  
  // Match events
  MatchStart = 'match_start',
  MatchEnd = 'match_end',
  RoundStart = 'round_start',
  RoundEnd = 'round_end',
  
  // Player events
  PlayerJoin = 'player_join',
  PlayerLeave = 'player_leave',
  HeroSelect = 'hero_select',
  
  // Gameplay events
  Kill = 'player_kill',
  Death = 'player_death',
  AbilityUse = 'ability_use',
  WeaponPurchase = 'weapon_purchase',
  
  // UI events
  SettingsOpen = 'settings_open',
  HowToPlayOpen = 'how_to_play_open',
  BuyMenuOpen = 'buy_menu_open',
  CreditsOpen = 'credits_open',
}

class AnalyticsServiceClass {
  private initialized = false;

  // Check if gtag is available
  private isAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  }

  // Initialize analytics (called on page load)
  initialize(): void {
    if (this.initialized) return;
    
    if (this.isAvailable()) {
      this.initialized = true;
      console.log('[Analytics] GA4 initialized');
    } else {
      console.warn('[Analytics] gtag not available - analytics disabled');
    }
  }

  // Track a custom event
  trackEvent(eventName: string | GameAnalyticsEvent, params?: Record<string, any>): void {
    if (!this.isAvailable()) return;

    try {
      window.gtag('event', eventName, {
        ...params,
        send_to: GA_MEASUREMENT_ID,
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Analytics] Event: ${eventName}`, params);
      }
    } catch (e) {
      console.warn('[Analytics] Failed to track event:', e);
    }
  }

  // -------------------------------------------------------------------------
  // Game-specific tracking methods
  // -------------------------------------------------------------------------

  // Track when a player starts hosting or joining
  trackGameStart(mode: 'host' | 'join', callsign: string): void {
    this.trackEvent(GameAnalyticsEvent.GameStart, {
      mode,
      callsign_length: callsign.length, // Don't send actual name for privacy
    });
  }

  // Track when player leaves the game
  trackGameEnd(durationSeconds: number, gamesPlayed: number): void {
    this.trackEvent(GameAnalyticsEvent.GameEnd, {
      duration_seconds: durationSeconds,
      games_played: gamesPlayed,
    });
  }

  // Track match start
  trackMatchStart(params: {
    gameMode: string;
    mapId: string;
    playerCount: number;
    botCount: number;
    isHost: boolean;
  }): void {
    this.trackEvent(GameAnalyticsEvent.MatchStart, {
      game_mode: params.gameMode,
      map_id: params.mapId,
      player_count: params.playerCount,
      bot_count: params.botCount,
      is_host: params.isHost,
    });
  }

  // Track match end
  trackMatchEnd(params: {
    gameMode: string;
    mapId: string;
    won: boolean;
    durationSeconds: number;
    kills: number;
    deaths: number;
    assists: number;
    heroId: string;
  }): void {
    this.trackEvent(GameAnalyticsEvent.MatchEnd, {
      game_mode: params.gameMode,
      map_id: params.mapId,
      won: params.won,
      duration_seconds: params.durationSeconds,
      kills: params.kills,
      deaths: params.deaths,
      assists: params.assists,
      hero_id: params.heroId,
      kd_ratio: params.deaths > 0 ? (params.kills / params.deaths).toFixed(2) : params.kills,
    });
  }

  // Track hero selection
  trackHeroSelect(heroId: string): void {
    this.trackEvent(GameAnalyticsEvent.HeroSelect, {
      hero_id: heroId,
    });
  }

  // Track ability usage
  trackAbilityUse(heroId: string, abilityName: string, abilitySlot: string): void {
    this.trackEvent(GameAnalyticsEvent.AbilityUse, {
      hero_id: heroId,
      ability_name: abilityName,
      ability_slot: abilitySlot,
    });
  }

  // Track weapon purchase
  trackWeaponPurchase(weaponId: string, price: number): void {
    this.trackEvent(GameAnalyticsEvent.WeaponPurchase, {
      weapon_id: weaponId,
      price,
    });
  }

  // Track UI interactions
  trackUIOpen(element: 'settings' | 'how_to_play' | 'buy_menu' | 'credits'): void {
    const eventMap = {
      settings: GameAnalyticsEvent.SettingsOpen,
      how_to_play: GameAnalyticsEvent.HowToPlayOpen,
      buy_menu: GameAnalyticsEvent.BuyMenuOpen,
      credits: GameAnalyticsEvent.CreditsOpen,
    };
    this.trackEvent(eventMap[element]);
  }

  // Track round completion
  trackRoundEnd(params: {
    roundNumber: number;
    won: boolean;
    teamScore: number;
    enemyScore: number;
  }): void {
    this.trackEvent(GameAnalyticsEvent.RoundEnd, {
      round_number: params.roundNumber,
      won: params.won,
      team_score: params.teamScore,
      enemy_score: params.enemyScore,
    });
  }

  // -------------------------------------------------------------------------
  // User properties (set once per session)
  // -------------------------------------------------------------------------

  setUserProperties(props: {
    preferredHero?: string;
    gamesPlayed?: number;
    totalPlayTime?: number;
  }): void {
    if (!this.isAvailable()) return;

    try {
      window.gtag('set', 'user_properties', props);
    } catch (e) {
      console.warn('[Analytics] Failed to set user properties:', e);
    }
  }
}

// Singleton instance
export const AnalyticsService = new AnalyticsServiceClass();

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
  // Wait for gtag to be available
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      AnalyticsService.initialize();
    });
  } else {
    AnalyticsService.initialize();
  }
}
